import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../../../lib/motion';
import { useSignedUrl } from '../../../services/uploads';
import { isPending } from '../../courses/editors/contentPlaceholder';
import ModuleViewContainer from './ModuleViewContainer';

export interface VideoViewProps {
  item: { video_url?: string; transcript?: string; title?: string };
  mode: 'learner' | 'preview';
}

const VideoView: React.FC<VideoViewProps> = ({ item, mode }) => {
  // S3-hosted uploads land here as a key (no scheme); external embeds
  // (YouTube/Vimeo/etc.) come in as http(s) URLs and pass through unchanged.
  const key = item.video_url && !isPending(item.video_url) && !item.video_url.startsWith('http')
    ? item.video_url
    : null;
  const { data: signedData } = useSignedUrl(key);
  const url = key
    ? signedData?.url ?? null
    : item.video_url && !isPending(item.video_url)
      ? item.video_url
      : null;

  const learnerBody = !url ? (
    <div className="flex-1 min-h-0 w-full flex items-center justify-center rounded-lg bg-surface-secondary border border-border">
      <span className="text-sm text-text-tertiary">No video</span>
    </div>
  ) : (
    <div className="flex-1 min-h-0 w-full flex flex-col gap-3">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <div
          className="aspect-video bg-black border border-border rounded-lg overflow-hidden"
          style={{ maxWidth: '100%', maxHeight: '100%', width: '100%' }}
        >
          <iframe
            src={url}
            className="w-full h-full block"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title="Video content"
          />
        </div>
      </div>
      {item.transcript && (
        <details className="rounded-lg border border-border bg-surface-primary shrink-0">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-text-secondary hover:text-text-primary select-none">
            View Transcript
          </summary>
          <div className="px-4 pb-4 text-sm text-text-secondary whitespace-pre-wrap max-h-48 overflow-y-auto">
            {item.transcript}
          </div>
        </details>
      )}
    </div>
  );

  const previewBody = !url ? (
    <div className="w-full aspect-video flex items-center justify-center rounded-lg bg-surface-secondary border border-border">
      <span className="text-sm text-text-tertiary">No video</span>
    </div>
  ) : (
    <div className="w-full space-y-3">
      <div className="aspect-video rounded-lg overflow-hidden bg-black border border-border">
        <iframe
          src={url}
          className="w-full h-full block"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          title="Video content"
        />
      </div>
      {item.transcript && (
        <details className="rounded-lg border border-border bg-surface-primary">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-text-secondary hover:text-text-primary select-none">
            View Transcript
          </summary>
          <div className="px-4 pb-4 text-sm text-text-secondary whitespace-pre-wrap">
            {item.transcript}
          </div>
        </details>
      )}
    </div>
  );

  if (mode === 'learner') {
    return (
      <motion.div {...fadeIn} className="w-full flex-1 flex flex-col min-h-0">
        <ModuleViewContainer padding="px-4 pb-4">
          {item.title && (
            <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-text-primary leading-tight">
                {item.title}
              </h2>
            </div>
          )}
          <div className="flex-1 min-h-0 flex flex-col px-3 sm:px-4 pb-3 sm:pb-4">
            {learnerBody}
          </div>
        </ModuleViewContainer>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="w-full">
      {previewBody}
    </motion.div>
  );
};

export default VideoView;
